import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import assistantService from '@services/assistant';
import { AssistantConversation, SendAssistantMessageInput } from '@models/assistant.model';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { useTranslation } from '@hooks/useTranslation';
import { ASSISTANT_CONVERSATION_QUERY_KEY } from './useAssistantConversation';
import { MY_ASSISTANT_CONVERSATIONS_QUERY_KEY } from './useMyAssistantConversations';
import { getAssistantErrorMessage } from './assistantErrorMessage';

/**
 * Sprint 32, story-165. Send/confirm/dismiss/delete — each seeds the conversation cache with the
 * returned row (mirroring `useProjectPlanDraftMutations.ts`'s `seedDraftCache`), query keys
 * include `organizationId`. None of these toast on success — the drawer/screen's own transcript
 * already communicates the result; only synchronous rejections (busy, cap, stale, etc.) toast.
 */
export const useAssistantMutations = () => {
  const queryClient = useQueryClient();
  const { organizationId } = useAppSelector((state: RootState) => state.organization);
  const tAssistant = useTranslation('assistant');

  const seedConversationCache = (conversation: AssistantConversation) => {
    queryClient.setQueryData(
      [ASSISTANT_CONVERSATION_QUERY_KEY, organizationId, conversation.conversationId],
      conversation,
    );
    void queryClient.invalidateQueries({ queryKey: [MY_ASSISTANT_CONVERSATIONS_QUERY_KEY, organizationId] });
  };

  const send = useMutation({
    mutationKey: ['sendAssistantMessage'],
    mutationFn: (input: SendAssistantMessageInput) => assistantService.sendAssistantMessage(input),
    onSuccess: seedConversationCache,
    onError: (error: any) => toast.error(getAssistantErrorMessage(error, tAssistant)),
  });

  const confirm = useMutation({
    mutationKey: ['confirmAssistantAction'],
    mutationFn: (conversationId: string) => assistantService.confirmAssistantAction(conversationId),
    onSuccess: seedConversationCache,
    onError: (error: any) => toast.error(getAssistantErrorMessage(error, tAssistant)),
  });

  const dismiss = useMutation({
    mutationKey: ['dismissAssistantAction'],
    mutationFn: (conversationId: string) => assistantService.dismissAssistantAction(conversationId),
    onSuccess: seedConversationCache,
    onError: (error: any) => toast.error(getAssistantErrorMessage(error, tAssistant)),
  });

  const remove = useMutation({
    mutationKey: ['deleteAssistantConversation'],
    mutationFn: (conversationId: string) => assistantService.deleteAssistantConversation(conversationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MY_ASSISTANT_CONVERSATIONS_QUERY_KEY, organizationId] });
    },
    onError: (error: any) => toast.error(getAssistantErrorMessage(error, tAssistant)),
  });

  return { send, confirm, dismiss, remove };
};

export default useAssistantMutations;
